import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { apiFetch } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Plus, Edit2, Trash2, Package, Clock, CheckCircle2, XCircle } from "lucide-react";
import { formatMXN } from "@/lib/format";
import { toast } from "sonner";

// Un producto sin `status` es de antes de la moderación: cuenta como publicado.
const STATUS = {
    pending: {
        label: "En revisión",
        icon: Clock,
        className: "bg-amber-50 text-amber-700 border-amber-200",
    },
    approved: {
        label: "Publicado",
        icon: CheckCircle2,
        className: "bg-emerald-50 text-emerald-700 border-emerald-200",
    },
    rejected: {
        label: "Rechazado",
        icon: XCircle,
        className: "bg-red-50 text-red-700 border-red-200",
    },
};

function StatusBadge({ status }) {
    const s = STATUS[status] || STATUS.approved;
    const Icon = s.icon;
    return (
        <span
            data-testid={`product-status-${status || "approved"}`}
            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-medium ${s.className}`}
        >
            <Icon className="w-3.5 h-3.5" aria-hidden="true" />
            {s.label}
        </span>
    );
}

export default function MerchantProducts() {
    const navigate = useNavigate();
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [hasStore, setHasStore] = useState(true);

    const load = async () => {
        try {
            const [p, s] = await Promise.all([
                apiFetch.get("/merchant/products"),
                apiFetch.get("/merchant/store"),
            ]);
            setProducts(p.data || []);
            setHasStore(!!s.data);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    const onDelete = async (id) => {
        if (!window.confirm("¿Eliminar este producto?")) return;
        try {
            await apiFetch.delete(`/merchant/products/${id}`);
            toast.success("Producto eliminado");
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "No se pudo eliminar");
        }
    };

    if (loading) {
        return <div className="h-64 rounded-xl bg-zinc-100 animate-pulse" />;
    }

    if (!hasStore) {
        return (
            <div data-testid="merchant-no-store" className="rounded-2xl border border-zinc-200 bg-white p-10 text-center">
                <div className="mx-auto w-12 h-12 rounded-full bg-zinc-100 grid place-items-center">
                    <Package className="w-5 h-5 text-zinc-400" />
                </div>
                <p className="mt-4 text-base font-semibold text-zinc-900">
                    Aún no tienes tienda
                </p>
                <p className="mt-1 text-sm text-zinc-500">
                    Crea tu tienda antes de subir productos.
                </p>
                <Button
                    className="mt-5 bg-primary hover:bg-primary-hover text-primary-foreground"
                    asChild
                >
                    <Link to="/dashboard/store">Crear mi tienda</Link>
                </Button>
            </div>
        );
    }

    return (
        <div data-testid="merchant-products" className="space-y-6">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <h1 className="text-3xl font-bold tracking-tight text-zinc-900">
                        Mis productos
                    </h1>
                    <p className="mt-2 text-sm text-zinc-500">
                        {products.length} producto{products.length !== 1 ? "s" : ""}
                    </p>
                </div>
                <Button
                    onClick={() => navigate("/dashboard/products/new")}
                    data-testid="merchant-new-product"
                    className="bg-primary hover:bg-primary-hover text-primary-foreground"
                >
                    <Plus className="w-4 h-4 mr-1" />
                    Nuevo producto
                </Button>
            </div>

            {products.length === 0 ? (
                <div className="rounded-2xl border-2 border-dashed border-zinc-200 p-12 text-center">
                    <div className="mx-auto w-12 h-12 rounded-full bg-zinc-100 grid place-items-center">
                        <Package className="w-5 h-5 text-zinc-400" />
                    </div>
                    <p className="mt-4 text-base font-semibold text-zinc-900">
                        Sin productos todavía
                    </p>
                    <p className="mt-1 text-sm text-zinc-500">
                        Sube tu primer producto y muéstralo al mundo.
                    </p>
                    <Button
                        className="mt-5 bg-primary hover:bg-primary-hover text-primary-foreground"
                        asChild
                    >
                        <Link to="/dashboard/products/new">Subir primer producto</Link>
                    </Button>
                </div>
            ) : (
                <div className="overflow-x-auto rounded-xl border border-zinc-200 bg-white">
                    <table className="w-full text-sm">
                        <thead className="bg-zinc-50 text-xs font-semibold text-zinc-500 uppercase tracking-wider">
                            <tr>
                                <th className="text-left px-4 py-3">Producto</th>
                                <th className="text-left px-4 py-3">Estado</th>
                                <th className="text-left px-4 py-3">Categoría</th>
                                <th className="text-left px-4 py-3">Precio</th>
                                <th className="text-left px-4 py-3">Stock</th>
                                <th className="text-right px-4 py-3">Acciones</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-zinc-100">
                            {products.map((p) => (
                                <tr key={p.id} data-testid={`merchant-product-${p.slug}`}>
                                    <td className="px-4 py-3">
                                        <div className="flex items-center gap-3">
                                            <img
                                                src={p.images?.[0]}
                                                alt=""
                                                className="w-10 h-10 rounded object-cover bg-zinc-100"
                                            />
                                            <div>
                                                <p className="font-medium text-zinc-900">
                                                    {p.name}
                                                </p>
                                                <p className="text-xs text-zinc-500">
                                                    /{p.storeSlug}/{p.slug}
                                                </p>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-4 py-3">
                                        <StatusBadge status={p.status} />
                                        {p.status === "rejected" && p.rejectedReason && (
                                            <p className="mt-1.5 text-xs text-red-700 max-w-[220px]">
                                                {p.rejectedReason}
                                            </p>
                                        )}
                                    </td>
                                    <td className="px-4 py-3 text-zinc-600 capitalize">
                                        {p.category}
                                    </td>
                                    <td className="px-4 py-3 text-zinc-900 font-medium">
                                        {formatMXN(p.price)}
                                    </td>
                                    <td className="px-4 py-3 text-zinc-600">
                                        {p.stock}
                                    </td>
                                    <td className="px-4 py-3 text-right">
                                        <button
                                            onClick={() =>
                                                navigate(`/dashboard/products/${p.id}/edit`)
                                            }
                                            data-testid={`merchant-edit-${p.slug}`}
                                            className="inline-flex items-center justify-center w-8 h-8 rounded-md text-zinc-500 hover:text-zinc-900 hover:bg-zinc-100 mr-1"
                                            aria-label="Editar"
                                        >
                                            <Edit2 className="w-4 h-4" />
                                        </button>
                                        <button
                                            onClick={() => onDelete(p.id)}
                                            data-testid={`merchant-delete-${p.slug}`}
                                            className="inline-flex items-center justify-center w-8 h-8 rounded-md text-zinc-500 hover:text-red-600 hover:bg-red-50"
                                            aria-label="Eliminar"
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
